import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, Building2, Eye, Archive, Trash2, Filter, Inbox } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('dotcall_admin_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const STATUS_META = {
  new: { label: 'New', className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  read: { label: 'Read', className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  archived: { label: 'Archived', className: 'bg-white/5 text-white/40 border-white/10' },
};

export default function AdminContactsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/contact-submissions`, authHeader());
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load contacts');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/admin/contact-submissions/${id}`, { status }, authHeader());
      setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      await axios.delete(`${API}/api/admin/contact-submissions/${id}`, authHeader());
      setItems(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const filtered = items.filter(i => {
    if (filter !== 'all' && i.status !== filter) return false;
    if (search && !`${i.name} ${i.email} ${i.company} ${i.message}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all: items.length,
    new: items.filter(i => i.status === 'new').length,
    read: items.filter(i => i.status === 'read').length,
    archived: items.filter(i => i.status === 'archived').length,
  };

  return (
    <div className="p-6 space-y-5" data-testid="admin-contacts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Contact Submissions</h1>
          <p className="text-sm text-white/50 mt-1">Messages from the website contact form</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <select value={filter} onChange={e => setFilter(e.target.value)} className="pl-9 pr-3 h-10 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-500/40" data-testid="contacts-filter">
              <option value="all" className="bg-[#0B1221]">All ({counts.all})</option>
              <option value="new" className="bg-[#0B1221]">New ({counts.new})</option>
              <option value="read" className="bg-[#0B1221]">Read ({counts.read})</option>
              <option value="archived" className="bg-[#0B1221]">Archived ({counts.archived})</option>
            </select>
          </div>
          <Input placeholder="Search name, email..." value={search} onChange={e => setSearch(e.target.value)} className="bg-white/[0.03] border-white/10 text-white w-64" data-testid="contacts-search" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: counts.all, color: 'from-white/5 to-white/[0.02]' },
          { label: 'New', value: counts.new, color: 'from-amber-500/15 to-amber-500/[0.02]' },
          { label: 'Read', value: counts.read, color: 'from-blue-500/15 to-blue-500/[0.02]' },
          { label: 'Archived', value: counts.archived, color: 'from-white/5 to-white/[0.02]' },
        ].map(s => (
          <div key={s.label} className={`p-4 rounded-xl bg-gradient-to-br ${s.color} border border-white/10`}>
            <div className="text-xs text-white/50 uppercase tracking-wider mb-1">{s.label}</div>
            <div className="text-2xl font-bold text-white">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* List */}
        <div className="lg:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-white/40">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-white/40"><Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" /> No messages</div>
          ) : (
            <div className="divide-y divide-white/[0.04] max-h-[70vh] overflow-y-auto" data-testid="contacts-list">
              {filtered.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setSelected(item); if (item.status === 'new') setStatus(item.id, 'read'); }}
                  className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors ${selected?.id === item.id ? 'bg-amber-500/10' : ''}`}
                  data-testid={`contact-item-${item.id}`}
                >
                  <div className="flex items-start justify-between mb-1 gap-2">
                    <div className="text-sm font-semibold text-white truncate">{item.name}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_META[item.status]?.className}`}>{STATUS_META[item.status]?.label}</span>
                  </div>
                  <div className="text-xs text-white/50 truncate">{item.subject}</div>
                  <div className="text-xs text-white/30 mt-1 truncate">{item.message}</div>
                  <div className="text-[10px] text-white/30 mt-1">{new Date(item.created_at).toLocaleString()}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 rounded-xl border border-white/10 bg-white/[0.02] p-6">
          {selected ? (
            <div className="space-y-4" data-testid="contact-detail">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-bold text-white">{selected.name}</h2>
                  <div className="text-xs text-white/50 mt-0.5">{new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_META[selected.status]?.className}`}>{STATUS_META[selected.status]?.label}</span>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/10 hover:border-amber-500/30 transition-colors"><Mail className="w-4 h-4 text-amber-300" /> <span className="truncate text-white">{selected.email}</span></a>
                {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/10 hover:border-amber-500/30 transition-colors"><Phone className="w-4 h-4 text-amber-300" /> <span className="truncate text-white">{selected.phone}</span></a>}
                {selected.company && <div className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/10"><Building2 className="w-4 h-4 text-amber-300" /> <span className="truncate text-white">{selected.company}</span></div>}
              </div>

              <div>
                <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Subject</div>
                <div className="text-sm text-white">{selected.subject}</div>
              </div>

              <div>
                <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Message</div>
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 text-sm text-white/85 whitespace-pre-wrap">{selected.message}</div>
              </div>

              <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
                <Button onClick={() => setStatus(selected.id, 'read')} size="sm" variant="outline" className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white"><Eye className="w-3.5 h-3.5 mr-1" /> Mark Read</Button>
                <Button onClick={() => setStatus(selected.id, 'archived')} size="sm" variant="outline" className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white"><Archive className="w-3.5 h-3.5 mr-1" /> Archive</Button>
                <Button onClick={() => remove(selected.id)} size="sm" variant="outline" className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                <a href={`mailto:${selected.email}?subject=Re:%20${encodeURIComponent(selected.subject)}`} className="ml-auto"><Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-white">Reply via Email</Button></a>
              </div>
            </div>
          ) : (
            <div className="text-center py-20 text-white/40"><Mail className="w-10 h-10 mx-auto mb-3 opacity-50" /><p>Select a submission to view details</p></div>
          )}
        </div>
      </div>
    </div>
  );
}
